import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Paths } from "@/paths";
import { OpLaunchPage } from "@/modules/opLaunchPages/OpLaunchPage";
import { Card, ErrorNote, PageHeader, ReadOnlyTag, SkeletonLines } from "@/shared/components/ds";
import type { ReportSummary } from "./models";
import { CommandListReports } from "./services/cmdListReports";
import styles from "./opReports.module.css";

/**
 * The preserved Periscope reports (MPN-2275): the list Ops and Analytics marked as needed
 * before Periscope was switched off, each served from the OP warehouse. A series of small
 * lists, one per Periscope dashboard they came from: the dashboard's name sits in the grey
 * uppercase strip a table head would, then one plain-weight row per report. A report's
 * parameters are asked for on its own page, not listed here.
 */
export default function OpReportsListPage() {
    const navigate = useNavigate();
    const [reports, setReports] = useState<ReportSummary[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        new CommandListReports().run().then(setReports).catch((e) => setError(e instanceof Error ? e.message : "Could not load the reports."));
    }, []);

    const dashboards = [...new Set((reports ?? []).map((r) => r.dashboard))];

    return (
        <OpLaunchPage title="OP Reports">
            <PageHeader title="OP Reports">
                <span className={styles.hint}>The Periscope reports kept after the old platform shut down.</span>
                <ReadOnlyTag />
            </PageHeader>
            {error && <ErrorNote>{error}</ErrorNote>}
            {!reports && !error && <Card title="Reports"><SkeletonLines /></Card>}
            {dashboards.map((dashboard) => {
                const group = reports!.filter((r) => r.dashboard === dashboard);
                return (
                    <Card key={dashboard}>
                        <div className={styles.groupHead}>{dashboard}</div>
                        {group.map((report) => {
                            const to = `/${Paths.OP_REPORTS}/${report.key}`;
                            return (
                                <div key={report.key} className={styles.reportRow} onClick={() => navigate(to)}>
                                    <div className={styles.reportTitle}>
                                        <Link to={to} onClick={(e) => e.stopPropagation()}>{report.title}</Link>
                                    </div>
                                    <div className={styles.chevron} aria-hidden="true">›</div>
                                </div>
                            );
                        })}
                    </Card>
                );
            })}
        </OpLaunchPage>
    );
}
